<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookAlignedPoints = [
  "Poem me Allama Iqbal strong faith ko Hazrat Ibrahim (A.S.) ki misaal se explain karte hain aur faithlessness ko moral decline ka sabab batate hain.",
  "Ruba'iyat ka major concern Muslim community ki spiritual weakness, disunity aur materialistic life ki taraf jhukao hai.",
  "Exam prep me faith, unity, self-respect aur Iqbal ke reform message ko samajhna sab se important hota hai.",
];

const chapterCoverage = [
  "Faith according to Iqbal",
  "Hazrat Ibrahim (A.S.) reference",
  "Weak faith and decline",
  "Muslim unity and self-respect",
  "Materialism critique",
  "Reformative message",
];

const quickRevisionPoints = [
  "Iqbal faith ko dignity aur moral courage ka source samajhte hain.",
  "Hazrat Ibrahim (A.S.) strong faith ka highest example hain.",
  "Weak faith spiritual decline aur loss of selfhood ka sabab banti hai.",
  "Poem Muslim disunity aur materialism ko criticize karti hai.",
  "Message spiritual revival aur unity ka hai.",
];

const readingQuestions = [
  {
    question: "What is faith according to Allama Iqbal in Ruba'iyat?",
    answer: "Iqbal ke nazdeek faith Allah ki marzi ke samne complete submission, self-respect aur divine love ka naam hai. Yeh insaan ko dignity, courage aur spiritual strength deta hai.",
  },
  {
    question: "Why does the poet refer to Hazrat Ibrahim (A.S.)?",
    answer: "Poet Hazrat Ibrahim (A.S.) ki misaal is liye dete hain kyun ke unhon ne intense trial ke bawajood apna faith nahi chhoda. Yeh example strong belief aur trust in Allah ki highest form ko show karta hai.",
  },
  {
    question: "Why does Iqbal say that having no faith is worse than slavery?",
    answer: "Iqbal ke mutabiq faith ke baghair insaan spiritually weak aur morally directionless ho jata hai. Aisi condition external slavery se bhi badtar hai kyun ke is me selfhood aur purpose dono lose ho jate hain.",
  },
  {
    question: "What problem of the Muslim world does the poem highlight?",
    answer: "Poem Muslim world ki disunity, weak faith, materialism aur lost self-respect ko highlight karti hai. Iqbal samajhte hain ke inhi reasons ki wajah se community apni original strength kho baithi hai.",
  },
  {
    question: "What message does the poem give to Muslims?",
    answer: "Poem Muslims ko jagane, faith mazboot karne, unity paida karne aur apni spiritual identity recover karne ka message deti hai. Iqbal ka tone reformative aur awakening-based hai.",
  },
];

const literaryPoints = [
  {
    label: "Tone",
    detail: "Tone serious, reformative, passionate aur exhortative hai. Poet complaint bhi karta hai aur motivate bhi karta hai.",
  },
  {
    label: "Theme",
    detail: "Major themes me faith, Muslim unity, self-respect, anti-materialism aur spiritual revival shamil hain.",
  },
  {
    label: "Imagery and Reference",
    detail: "Hazrat Ibrahim (A.S.) at the stake wali image faith ke power ko symbolize karti hai. Yeh image poem ko historical aur spiritual depth deti hai.",
  },
  {
    label: "Message",
    detail: "Poem ka message yeh hai ke nations ki real strength un ke faith aur unity me hoti hai, sirf worldly progress me nahi.",
  },
];

const mcqs = [
  {
    question: "Why does Iqbal refer to Hazrat Ibrahim (A.S.) in the poem?",
    options: [
      { label: "a", text: "to describe political power only", verdict: "incorrect", reason: "Reference ka main point spiritual courage aur faith hai, political power nahi." },
      { label: "b", text: "to show the highest example of firm faith", verdict: "correct", reason: "Hazrat Ibrahim (A.S.) ka zikr steadfast belief aur trust in Allah ko symbolize karta hai." },
      { label: "c", text: "to praise material success", verdict: "incorrect", reason: "Poem materialism ko criticize karti hai." },
      { label: "d", text: "to reject sacrifice", verdict: "incorrect", reason: "Reference actually sacrifice aur commitment ko elevate karta hai." },
    ],
  },
  {
    question: "According to the poem, lack of faith leads to:",
    options: [
      { label: "a", text: "spiritual weakness and loss of selfhood", verdict: "correct", reason: "Iqbal ke mutabiq faith ke baghair dignity, direction, aur inner strength weak ho jati hai." },
      { label: "b", text: "greater unity among Muslims", verdict: "incorrect", reason: "Poem weak faith ko disunity ke causes me count karti hai." },
      { label: "c", text: "higher moral courage", verdict: "incorrect", reason: "Moral courage faith se strengthen hota hai, us ki absence se nahi." },
      { label: "d", text: "freedom from all responsibility", verdict: "incorrect", reason: "Poem self-respect aur responsibility ko positive values ke tor par present karti hai." },
    ],
  },
];

const answerBuilding = [
  "Faith wale answer me Allah par trust, self-respect aur moral courage tino mention karo.",
  "Muslim decline wale jawab me materialism, disunity aur weak belief ko connect karo.",
  "Message ya theme me Iqbal ko reformer poet ke tor par present karna strong answer banata hai.",
  "Hazrat Ibrahim (A.S.) wale answer me trial aur steadfastness dono mention karo.",
];

usePageSeo({
  title: "Ruba'iyat Notes | 11th Class English Unit 13",
  description: "11th class English Unit 13 Ruba'iyat notes with faith, unity, spiritual message, tone, explanation and MCQ support.",
  keywords: "rubaiyat notes, 11th class english unit 13, class 11 rubaiyat poem notes, rubaiyat summary, faith and unity poem notes",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Poems", item: "/11th/english/poems/" },
    { name: "Ruba'iyat", item: "/11th/english/poems/rubaiyat/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 13"
    title="Ruba'iyat"
    intro="Yeh page poem ke faith, Muslim unity aur spiritual awakening ke message ko exam prep format me explain karta hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookAlignedPoints },
      { title: 'Unit 13 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Answer-Building Notes', items: answerBuilding }
    ]"
    :text-sections="[
      { title: 'Reading and Explanation Answers', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Literary Points', items: literaryPoints.map((item) => ({ title: item.label, description: item.detail })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





